/**
 * 睡眠定时器：sleepTimer、sleepTimerMinutes、setSleepTimerHandler
 * 抽离自 MusicPlayer.js
 */
import { useState, useCallback } from 'react';
import { runFadeOut } from '../fadeUtils';

/**
 * @param {object} options
 * @param {number} options.initialSleepTimerMinutes - 初始分钟数（如 savedSettings?.sleepTimerMinutes ?? 0）
 * @param {boolean} options.hasVideo
 * @param {function(boolean, React.RefObject, React.RefObject): HTMLMediaElement|null} options.getActiveMediaElement
 * @param {React.RefObject} options.videoRef
 * @param {React.RefObject} options.audioRef
 * @param {function(boolean): void} options.updatePlaying
 * @param {boolean} options.fadeInOut
 * @param {function(string): string} options.t - i18n t
 * @returns {{ sleepTimer: ReturnType<typeof setTimeout>|null, sleepTimerMinutes: number, setSleepTimerHandler: function(number): void }}
 */
export function useSleepTimer({
  initialSleepTimerMinutes = 0,
  hasVideo,
  getActiveMediaElement,
  videoRef,
  audioRef,
  updatePlaying,
  fadeInOut,
  t
}) {
  const [sleepTimer, setSleepTimer] = useState(null);
  const [sleepTimerMinutes, setSleepTimerMinutes] = useState(initialSleepTimerMinutes);

  const setSleepTimerHandler = useCallback((minutes) => {
    if (sleepTimer) {
      clearTimeout(sleepTimer);
      setSleepTimer(null);
      setSleepTimerMinutes(0);
      return;
    }

    if (minutes <= 0) return;

    setSleepTimerMinutes(minutes);
    const timer = setTimeout(() => {
      const mediaElement = getActiveMediaElement(hasVideo, videoRef, audioRef);
      if (!mediaElement) return;

      if (hasVideo) {
        try { mediaElement.pause(); } catch (_) {}
        updatePlaying(false);
      } else if (fadeInOut) {
        const mediaEl = getActiveMediaElement(hasVideo, videoRef, audioRef);
        if (mediaEl) {
          runFadeOut(mediaEl, () => {
            mediaEl.pause();
            updatePlaying(false);
          });
        }
      } else {
        try { mediaElement.pause(); } catch (_) {}
        updatePlaying(false);
      }
      setSleepTimer(null);
      setSleepTimerMinutes(0);
      alert(t('hints.sleepTimerStopped'));
    }, minutes * 60 * 1000);

    setSleepTimer(timer);
  }, [sleepTimer, hasVideo, getActiveMediaElement, videoRef, audioRef, updatePlaying, fadeInOut, t]);

  return { sleepTimer, sleepTimerMinutes, setSleepTimerHandler };
}
